/**
 * CC4Pair — pair preview screen.
 * data-screen-label="cc-04 Pair preview"
 *
 * - Side-by-side CCAvatar pair, pair-tone backdrop
 * - 4-bullet feature list
 * - CTA "See compatibility · 60 ⭐" → PAY event
 */

import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { CCBackdrop, CCStars, CCAvatar } from './shared';
import { useChart } from '../../../hooks/useChart';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import { capitalizeSign } from '../../me/chartViewAdapter';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// Feature list items (i18n key pairs: [titleKey, subKey])
// --------------------------------------------------------------------------

const FEATURES: Array<[string, string]> = [
  ['compat.pair.features.f1.title', 'compat.pair.features.f1.sub'],
  ['compat.pair.features.f2.title', 'compat.pair.features.f2.sub'],
  ['compat.pair.features.f3.title', 'compat.pair.features.f3.sub'],
  ['compat.pair.features.f4.title', 'compat.pair.features.f4.sub'],
];

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC4PairProps {
  flow: UseCompatFlowReturn;
}

export function CC4Pair({ flow }: CC4PairProps): JSX.Element {
  const { t } = useTranslation('flows');
  const { partner, isGift, searchUsername } = flow;
  // Big-3 initials: the first letter of each sign name in the user's language (the server sends «leo»).
  const initial = (s: string | null | undefined): string => {
    if (!s) return '?';
    const cap = capitalizeSign(s.trim().toLowerCase());
    return t(`me:natalChart.names.signs.${cap}`, { defaultValue: cap }).charAt(0) || '?';
  };
  const initials = (a: string, b: string, c: string): string => `${initial(a)} ${initial(b)} ${initial(c)}`;

  // Off-platform gift: recipient not found on Calestea — we still let the buyer
  // pay, and the recipient joins + consents via the share link afterward. No
  // archetype/Big-3 to preview, so show their @handle and a short note instead.
  const offPlatform = isGift && !partner && Boolean(searchUsername);
  const partnerLabel = partner?.displayName ?? (offPlatform ? `@${searchUsername}` : t('compat.pair.partnerFallback'));
  const partnerArchetypeLabel = partner?.archetype ?? (offPlatform ? `@${searchUsername}` : t('compat.pair.partnerFallback'));

  // The current user's own archetype + Big-3 come from their natal chart
  // (the compat flow only carries the partner). Falls back gracefully while
  // the chart loads or if it isn't computed yet.
  const { data: chart } = useChart();
  const myChart = chart?.status === 'ready' ? chart : null;
  const myArchetype = myChart?.archetype?.label ?? null;
  const mySigns = myChart
    ? initials(myChart.sun, myChart.moon, myChart.ascendant)
    : '— — —';

  function handlePay() {
    flow.send({ type: 'PAY' });
  }

  // Fallback glyph / palette for "me" (the user themselves)
  const myGlyph = '🌙';
  const myPalette = 'gold' as const;
  const partnerGlyph = partner?.glyph ?? '✦';
  const partnerPalette = partner?.palette ?? 'violet';

  return (
    <div className={styles.root} data-screen-label="cc-04 Pair preview">
      <CCBackdrop tone="pair" />
      <CCStars seed={4} count={60} />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('flows:compat.headerTitle')} onBack={() => flow.send({ type: 'RETRY' })} headingId="compat-heading" />

        {/* Title */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <div className={styles.eyebrow}>{isGift ? t('compat.gift.pairEyebrow') : t('compat.pair.eyebrow')}</div>
          <h1 className={styles.displayTitleSm}>
            {myArchetype ?? t('compat.pair.me')} ×{' '}
            <em className={styles.gradAccentCool}>{partnerArchetypeLabel}</em>
          </h1>
        </div>

        {/* Side-by-side avatars with blend overlay */}
        <div className={styles.pairAvatarsWrap}>
          {/* Blend glow behind */}
          <div className={styles.pairBlend}>
            <svg width="240" height="200" viewBox="0 0 240 200" aria-hidden="true">
              <defs>
                <radialGradient id="ccBlend" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#f0c850" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#7a3dff" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx="120" cy="100" r="56" fill="url(#ccBlend)" />
            </svg>
          </div>

          <div className={styles.pairAvatarsRow}>
            <div style={{ transform: 'rotate(-6deg) translateX(8px)' }}>
              <CCAvatar glyph={myGlyph} palette={myPalette} size={132} ringed />
            </div>
            <div style={{ transform: 'rotate(6deg) translateX(-8px)' }}>
              <CCAvatar glyph={partnerGlyph} palette={partnerPalette} size={132} ringed />
            </div>
          </div>
        </div>

        {/* Names row */}
        <div className={styles.pairNamesGrid}>
          <div className={styles.pairNameLeft}>
            <div className={styles.pairNameHandle}>{t('compat.pair.me')}</div>
            <div className={styles.pairNameArchetype}>{myArchetype ?? '—'}</div>
            <div className={styles.pairNameSigns}>{mySigns}</div>
          </div>
          <div className={styles.pairTimes}>×</div>
          <div className={styles.pairNameRight}>
            <div className={[styles.pairNameHandle, styles.pairNameHandleViolet].join(' ')}>
              {partnerLabel}
            </div>
            <div className={styles.pairNameArchetype}>{partner?.archetype ?? '—'}</div>
            <div className={styles.pairNameSigns}>
              {partner?.big3
                ? initials(partner.big3.sun, partner.big3.moon, partner.big3.rising)
                : '— — —'}
            </div>
          </div>
        </div>

        {/* Off-platform gift note */}
        {offPlatform && (
          <div className={styles.noChargeNote}>
            {t('compat.gift.offPlatformNote', { name: `@${searchUsername}` })}
          </div>
        )}

        {/* Feature list */}
        <div className={styles.featureList}>
          <div className={styles.featureListLabel}>{t('compat.pair.featuresLabel')}</div>
          {FEATURES.map(([titleKey, subKey]) => (
            <div key={titleKey} className={styles.featureItem}>
              <span className={styles.featureCheck}>✓</span>
              <div>
                <div className={styles.featureTitle}>{t(titleKey)}</div>
                <div className={styles.featureSub}>{t(subKey)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handlePay}
            data-testid="cc4-pay-btn"
          >
            {isGift
              ? t('compat.gift.payCta', { stars: SKU_CATALOG['gifting_crush'].stars })
              : t('compat.pair.cta', { stars: SKU_CATALOG['compat_check'].stars })}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
